/**
 * BizOne — Industry Showcase Carousel.
 *
 * Purely a public-marketing component. One authoritative `activeSlide` state
 * drives everything: tabs, dots, arrows, swipe, and auto-rotation. Backend is
 * never contacted; all data is sanitized demo content imported from
 * `industryShowcaseData.js`.
 *
 * Behaviors:
 *   - Auto-rotate every 7s. Pauses on hover, document.hidden, user drag,
 *     keyboard focus inside carousel, and prefers-reduced-motion.
 *   - Manual nav (arrow / dot / tab) resets the rotation timer cleanly.
 *   - Horizontal swipe only when gesture is clearly horizontal and passes
 *     the distance threshold. Vertical scrolling is never blocked.
 *   - Keyboard: ArrowLeft/ArrowRight navigate when controls are focused.
 */
import { useEffect, useMemo, useRef, useState, useCallback, useId } from "react";
import { ChevronLeft, ChevronRight } from "lucide-react";
import { INDUSTRY_SHOWCASE, SLIDE_COUNT } from "@/pages/landing/industryShowcaseData";
import { DemoTabletPOS, DemoInventoryPanel } from "@/pages/landing/DemoDevices";

const AUTOPLAY_MS = 7000;
const SWIPE_THRESHOLD_PX = 50;
const SWIPE_RATIO = 1.2; // |dx| must exceed |dy| * ratio to count

function usePrefersReducedMotion() {
  const [reduced, setReduced] = useState(false);
  useEffect(() => {
    if (typeof window === "undefined" || !window.matchMedia) return;
    const mq = window.matchMedia("(prefers-reduced-motion: reduce)");
    const update = () => setReduced(mq.matches);
    update();
    if (mq.addEventListener) mq.addEventListener("change", update);
    else mq.addListener(update);
    return () => {
      if (mq.removeEventListener) mq.removeEventListener("change", update);
      else mq.removeListener(update);
    };
  }, []);
  return reduced;
}

function useDocumentHidden() {
  const [hidden, setHidden] = useState(() =>
    typeof document !== "undefined" ? document.hidden : false);
  useEffect(() => {
    const on = () => setHidden(document.hidden);
    document.addEventListener("visibilitychange", on);
    return () => document.removeEventListener("visibilitychange", on);
  }, []);
  return hidden;
}

export default function IndustryShowcaseCarousel() {
  const reducedMotion = usePrefersReducedMotion();
  const docHidden = useDocumentHidden();
  const headingId = useId();
  const regionId = useId();

  const [active, setActive] = useState(0);
  const [hovered, setHovered] = useState(false);
  const [dragging, setDragging] = useState(false);
  const [focusInside, setFocusInside] = useState(false);
  const containerRef = useRef(null);
  const touchStartRef = useRef(null);

  const wrap = useCallback((n) => ((n % SLIDE_COUNT) + SLIDE_COUNT) % SLIDE_COUNT, []);
  const goTo = useCallback((i) => setActive(wrap(i)), [wrap]);
  const next = useCallback(() => setActive((v) => wrap(v + 1)), [wrap]);
  const prev = useCallback(() => setActive((v) => wrap(v - 1)), [wrap]);

  // Auto-rotation — a single interval that respects all pause conditions.
  const canAutoplay = !reducedMotion && !docHidden && !hovered && !dragging && !focusInside;
  useEffect(() => {
    if (!canAutoplay) return undefined;
    const id = setInterval(() => {
      setActive((v) => (v + 1) % SLIDE_COUNT);
    }, AUTOPLAY_MS);
    return () => clearInterval(id);
    // `active` is NOT in the dependency list: manual navigation resets the
    // timer because setActive triggers a re-render which re-runs this effect
    // via its own deps (canAutoplay stays true but the interval remounts via
    // the ref below).
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [canAutoplay, active]);

  // Touch / swipe — recorded on the carousel region. We only treat the gesture
  // as a horizontal swipe when it is clearly horizontal AND exceeds threshold.
  function onTouchStart(e) {
    if (!e.touches || !e.touches[0]) return;
    touchStartRef.current = { x: e.touches[0].clientX, y: e.touches[0].clientY };
    setDragging(true);
  }
  function onTouchEnd(e) {
    const start = touchStartRef.current;
    touchStartRef.current = null;
    setDragging(false);
    if (!start || !e.changedTouches || !e.changedTouches[0]) return;
    const dx = e.changedTouches[0].clientX - start.x;
    const dy = e.changedTouches[0].clientY - start.y;
    if (Math.abs(dx) < SWIPE_THRESHOLD_PX) return;
    if (Math.abs(dx) < Math.abs(dy) * SWIPE_RATIO) return;
    if (dx < 0) next(); else prev();
  }

  // Keyboard — ArrowLeft/Right while the carousel region (or its children)
  // has focus. We do NOT hijack global keys.
  function onKeyDown(e) {
    if (e.key === "ArrowLeft") { e.preventDefault(); prev(); }
    else if (e.key === "ArrowRight") { e.preventDefault(); next(); }
  }

  // Pointer-only hover pause (ignore devices without hover).
  function onPointerEnter(e) {
    if (e.pointerType === "mouse" || e.pointerType === "pen") setHovered(true);
  }
  function onPointerLeave(e) {
    if (e.pointerType === "mouse" || e.pointerType === "pen") setHovered(false);
  }

  const slide = INDUSTRY_SHOWCASE[active];
  const transition = useMemo(
    () => (reducedMotion ? "none" : "opacity 300ms ease, transform 300ms ease"),
    [reducedMotion]);

  return (
    <section
      id="industries"
      className="relative border-b border-border bg-background scroll-mt-20"
      data-testid="industry-showcase-carousel"
      aria-labelledby={headingId}
    >
      <div className="max-w-6xl mx-auto px-5 lg:px-10 py-16 lg:py-20">
        {/* Heading */}
        <div className="max-w-2xl">
          <div className="text-[10px] uppercase tracking-widest text-brand font-medium">Showcase</div>
          <h2 id={headingId} className="mt-2 font-display text-3xl sm:text-4xl tracking-tighter">
            One system. Different businesses.
          </h2>
          <p className="mt-3 text-base text-muted-foreground">
            From sari-sari stores to coffee shops, bigasan and restaurants, BizOne adapts
            to the way Filipino businesses sell and manage inventory.
          </p>
        </div>

        {/* Industry tabs */}
        <div className="mt-6 flex gap-2 flex-wrap" role="tablist" aria-label="Choose industry">
          {INDUSTRY_SHOWCASE.map((it, i) => {
            const isActive = i === active;
            return (
              <button
                key={it.key}
                role="tab"
                aria-selected={isActive}
                aria-controls={regionId}
                onClick={() => goTo(i)}
                data-testid={`industry-tab-${it.key}`}
                className={`text-xs sm:text-sm px-3 py-1.5 rounded-full border transition-colors ${
                  isActive
                    ? "bg-foreground text-background border-foreground"
                    : "bg-background border-border text-muted-foreground hover:text-foreground hover:bg-muted"
                }`}
              >
                {it.label}
              </button>
            );
          })}
        </div>

        {/* Carousel region */}
        <div
          ref={containerRef}
          id={regionId}
          role="region"
          aria-roledescription="carousel"
          aria-label="BizOne across different Filipino business types"
          tabIndex={0}
          data-testid="industry-carousel-region"
          className="mt-6 relative rounded-2xl border border-border bg-card overflow-hidden focus:outline-none focus-visible:ring-2 focus-visible:ring-brand"
          onPointerEnter={onPointerEnter}
          onPointerLeave={onPointerLeave}
          onFocusCapture={() => setFocusInside(true)}
          onBlurCapture={(e) => {
            if (!e.currentTarget.contains(e.relatedTarget)) setFocusInside(false);
          }}
          onKeyDown={onKeyDown}
          onTouchStart={onTouchStart}
          onTouchEnd={onTouchEnd}
          onTouchCancel={() => { touchStartRef.current = null; setDragging(false); }}
        >
          <div className={`absolute inset-0 bg-gradient-to-br ${slide.accent} pointer-events-none`} />
          <div className="relative p-5 lg:p-8">
            <div
              key={slide.key}
              style={{ transition }}
              className="opacity-100"
              data-testid={`industry-slide-${slide.key}`}
            >
              {/* Industry mini-head */}
              <div className="flex items-start justify-between gap-3 flex-wrap">
                <div>
                  <span className={`inline-flex items-center text-[10px] uppercase tracking-widest font-semibold px-2 py-0.5 rounded-full ${slide.chipBg}`}>
                    {slide.label}
                  </span>
                  <p className="mt-2 text-sm sm:text-base text-foreground/90 max-w-xl leading-relaxed">
                    {slide.tagline}
                  </p>
                </div>
              </div>

              {/* Devices — vertical stack on mobile, side-by-side on md+ */}
              <div className="mt-6 grid gap-6 md:gap-8 md:grid-cols-5 items-center">
                <div className="md:col-span-3">
                  <DemoTabletPOS data={slide.pos} label={slide.key} />
                </div>
                <div className="md:col-span-2">
                  <DemoInventoryPanel data={slide.panel} label={slide.key} />
                </div>
              </div>
            </div>

            {/* Nav arrows — hidden on very small screens where swipe is primary */}
            <button
              type="button"
              aria-label="Previous industry"
              onClick={prev}
              data-testid="industry-prev"
              className="hidden sm:flex absolute left-2 top-1/2 -translate-y-1/2 h-10 w-10 rounded-full border border-border bg-background/90 backdrop-blur items-center justify-center shadow-sm hover:bg-background"
            >
              <ChevronLeft className="h-5 w-5" />
            </button>
            <button
              type="button"
              aria-label="Next industry"
              onClick={next}
              data-testid="industry-next"
              className="hidden sm:flex absolute right-2 top-1/2 -translate-y-1/2 h-10 w-10 rounded-full border border-border bg-background/90 backdrop-blur items-center justify-center shadow-sm hover:bg-background"
            >
              <ChevronRight className="h-5 w-5" />
            </button>
          </div>
        </div>

        {/* Dots */}
        <div className="mt-5 flex items-center justify-center gap-2" aria-label="Carousel pagination">
          {INDUSTRY_SHOWCASE.map((it, i) => {
            const isActive = i === active;
            return (
              <button
                key={it.key}
                type="button"
                aria-label={`Show ${it.label}`}
                aria-current={isActive ? "true" : "false"}
                onClick={() => goTo(i)}
                data-testid={`industry-dot-${it.key}`}
                className={`h-2 rounded-full transition-all ${
                  isActive ? "w-6 bg-foreground" : "w-2 bg-muted-foreground/40 hover:bg-muted-foreground/70"
                }`}
              />
            );
          })}
        </div>

        {/* A11y footnote */}
        <p className="mt-3 text-center text-[11px] text-muted-foreground">
          Marketing examples with sanitized demo data. Same BizOne system — different industries.
        </p>
      </div>
    </section>
  );
}
